import { useState, useRef, useEffect } from 'react';
import AppLogo from '../components/shared/AppLogo.jsx';
import { lookupFirms, mfaVerify, mfaSetup } from './grcClient.js';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import QRCode from 'react-qr-code';

// ── Shared field wrapper ──────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label className="section-label" style={{ display: 'block', marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

// ── Error banner ──────────────────────────────────────────────────────────────
function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div
      style={{
        padding: '8px 12px',
        borderRadius: 8,
        marginBottom: 16,
        background: 'var(--danger-bg)',
        border: '1px solid var(--danger)',
        color: 'var(--danger)',
        fontSize: 12,
      }}
    >
      {message}
    </div>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────
function Header() {
  return (
    <div style={{ textAlign: 'center', marginBottom: 40 }}>
      <AppLogo size={52} borderRadius={14} style={{ margin: '0 auto 16px' }} />
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: 'var(--fg)',
          fontFamily: "'Syne', 'Roboto Slab', sans-serif",
        }}
      >
        Tryggr <span style={{ color: 'var(--accent)' }}>GRC</span>
      </div>
      <div className="section-label" style={{ marginTop: 4 }}>
        AUDITOR PORTAL
      </div>
    </div>
  );
}

// ── TOTP verification screen ──────────────────────────────────────────────────
function TotpVerifyScreen({ challengeToken, onSuccess, onBack }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code || code.length < 6) return;
    setLoading(true);
    setError('');
    try {
      const data = await mfaVerify('totp', { challengeToken, code });
      if (data.error) {
        setError(data.error);
        return;
      }
      onSuccess(data.token, data.user);
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ padding: 32, width: '100%', maxWidth: 400 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)', marginBottom: 4 }}>
        Two-factor verification
      </div>
      <div className="section-label" style={{ marginBottom: 20 }}>
        Enter the 6-digit code from your authenticator app
      </div>
      <ErrorBanner message={error} />
      <form onSubmit={submit}>
        <Field label="Authentication code">
          <input
            className="input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            autoFocus
            placeholder="000000"
            style={{ letterSpacing: '0.2em', fontSize: 18, textAlign: 'center' }}
          />
        </Field>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || code.length < 6}
          style={{ width: '100%', marginTop: 8, justifyContent: 'center' }}
        >
          {loading ? 'Verifying…' : 'Verify'}
        </button>
      </form>
      <button
        onClick={onBack}
        style={{
          marginTop: 16,
          background: 'none',
          border: 'none',
          color: 'var(--fg3)',
          fontSize: 12,
          cursor: 'pointer',
          fontFamily: 'var(--font-mono)',
          padding: 0,
        }}
      >
        ← Back
      </button>
    </div>
  );
}

// ── WebAuthn verification screen ──────────────────────────────────────────────
function WebAuthnVerifyScreen({ challengeToken, onSuccess, onBack }) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const attempted = useRef(false);

  const verify = async () => {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const optData = await mfaVerify('webauthn-options', { challengeToken });
      if (optData.error) {
        setError(optData.error);
        return;
      }

      const authResponse = await startAuthentication({
        optionsJSON: optData.authenticationOptions,
      });

      const verifyData = await mfaVerify('webauthn-verify', {
        authToken: optData.authToken,
        authenticationResponse: authResponse,
        origin: window.location.origin,
      });
      if (verifyData.error) {
        setError(verifyData.error);
        return;
      }
      onSuccess(verifyData.token, verifyData.user);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey prompt was dismissed. Click below to try again.');
      } else {
        setError(err.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!attempted.current) {
      attempted.current = true;
      verify();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="card"
      style={{ padding: 32, width: '100%', maxWidth: 400, textAlign: 'center' }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)', marginBottom: 4 }}>
        Verify with passkey
      </div>
      <div className="section-label" style={{ marginBottom: 24 }}>
        Use your device passkey to complete sign-in
      </div>
      <ErrorBanner message={error} />
      <button
        className="btn btn-primary"
        onClick={verify}
        disabled={loading}
        style={{ width: '100%', justifyContent: 'center' }}
      >
        {loading ? 'Waiting for passkey…' : 'Use passkey'}
      </button>
      <button
        onClick={onBack}
        style={{
          marginTop: 16,
          background: 'none',
          border: 'none',
          color: 'var(--fg3)',
          fontSize: 12,
          cursor: 'pointer',
          fontFamily: 'var(--font-mono)',
          padding: 0,
        }}
      >
        ← Back
      </button>
    </div>
  );
}

// ── TOTP setup screen ─────────────────────────────────────────────────────────
function TotpSetupScreen({ challengeToken, onSuccess, onBack }) {
  const [step, setStep] = useState('loading'); // 'loading' | 'scan' | 'confirm'
  const [otpauthUri, setOtpauthUri] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    mfaSetup('totp-generate', { challengeToken })
      .then((data) => {
        if (data.error) {
          setError(data.error);
          setStep('scan');
          return;
        }
        setOtpauthUri(data.otpauthUri);
        setSetupToken(data.setupToken);
        setStep('scan');
      })
      .catch((err) => {
        setError(err.message);
        setStep('scan');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async (e) => {
    e.preventDefault();
    if (!code || code.length < 6) return;
    setLoading(true);
    setError('');
    try {
      const data = await mfaSetup('totp-confirm', { setupToken, code });
      if (data.error) {
        setError(data.error);
        return;
      }
      onSuccess(data.token, data.user);
    } catch (err) {
      setError(err.message || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'loading') {
    return (
      <div
        className="card"
        style={{ padding: 32, width: '100%', maxWidth: 400, textAlign: 'center' }}
      >
        <div className="section-label">Generating authenticator setup…</div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 32, width: '100%', maxWidth: 400 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)', marginBottom: 4 }}>
        Set up authenticator app
      </div>
      <div className="section-label" style={{ marginBottom: 16 }}>
        Scan with Microsoft Authenticator, Google Authenticator, or similar
      </div>
      <ErrorBanner message={error} />
      {otpauthUri && (
        <div
          style={{
            background: '#fff',
            borderRadius: 8,
            padding: 16,
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 20,
          }}
        >
          <QRCode value={otpauthUri} size={160} />
        </div>
      )}
      <form onSubmit={confirm}>
        <Field label="Enter the 6-digit code to confirm">
          <input
            className="input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            style={{ letterSpacing: '0.2em', fontSize: 18, textAlign: 'center' }}
          />
        </Field>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || code.length < 6}
          style={{ width: '100%', marginTop: 8, justifyContent: 'center' }}
        >
          {loading ? 'Confirming…' : 'Confirm setup'}
        </button>
      </form>
      <button
        onClick={onBack}
        style={{
          marginTop: 16,
          background: 'none',
          border: 'none',
          color: 'var(--fg3)',
          fontSize: 12,
          cursor: 'pointer',
          fontFamily: 'var(--font-mono)',
          padding: 0,
        }}
      >
        ← Back
      </button>
    </div>
  );
}

// ── WebAuthn setup screen ─────────────────────────────────────────────────────
function WebAuthnSetupScreen({ challengeToken, onSuccess, onSkipToTotp, onBack }) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const attempted = useRef(false);

  const register = async () => {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const optData = await mfaSetup('webauthn-options', { challengeToken });
      if (optData.error) {
        setError(optData.error);
        return;
      }

      const regResponse = await startRegistration({ optionsJSON: optData.registrationOptions });

      const verifyData = await mfaSetup('webauthn-verify', {
        setupToken: optData.setupToken,
        registrationResponse: regResponse,
        origin: window.location.origin,
      });
      if (verifyData.error) {
        setError(verifyData.error);
        return;
      }
      onSuccess(verifyData.token, verifyData.user);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Passkey prompt was dismissed.');
      } else {
        setError(err.message || 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!attempted.current) {
      attempted.current = true;
      register();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="card"
      style={{ padding: 32, width: '100%', maxWidth: 400, textAlign: 'center' }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)', marginBottom: 4 }}>
        Register a passkey
      </div>
      <div className="section-label" style={{ marginBottom: 24 }}>
        Use Touch ID, Face ID, Windows Hello, or a security key
      </div>
      <ErrorBanner message={error} />
      <button
        className="btn btn-primary"
        onClick={register}
        disabled={loading}
        style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }}
      >
        {loading ? 'Waiting for passkey…' : 'Register passkey'}
      </button>
      <button
        className="btn"
        onClick={onSkipToTotp}
        style={{ width: '100%', justifyContent: 'center' }}
      >
        Use authenticator app instead
      </button>
      <button
        onClick={onBack}
        style={{
          marginTop: 16,
          background: 'none',
          border: 'none',
          color: 'var(--fg3)',
          fontSize: 12,
          cursor: 'pointer',
          fontFamily: 'var(--font-mono)',
          padding: 0,
        }}
      >
        ← Back
      </button>
    </div>
  );
}

// ── MFA setup choice screen ───────────────────────────────────────────────────
export function MfaSetupScreen({ challengeToken, onSuccess, onBack }) {
  const [method, setMethod] = useState('webauthn'); // 'webauthn' | 'totp'

  const handleSuccess = (token, user) => onSuccess(token, user);

  if (method === 'totp') {
    return (
      <TotpSetupScreen
        challengeToken={challengeToken}
        onSuccess={handleSuccess}
        onBack={() => setMethod('webauthn')}
      />
    );
  }

  return (
    <WebAuthnSetupScreen
      challengeToken={challengeToken}
      onSuccess={handleSuccess}
      onSkipToTotp={() => setMethod('totp')}
      onBack={onBack}
    />
  );
}

// ── Main GrcLogin component ───────────────────────────────────────────────────
/**
 * Multi-step GRC auditor login:
 *   1. email-entry  — enter email, look up which firms the user belongs to
 *   2. firm-pick    — shown only when the user belongs to multiple firms
 *   3. password     — enter password for the chosen firm
 *   4. mfa-challenge — verify TOTP or WebAuthn (if MFA enrolled)
 *   5. mfa-setup    — enroll MFA (if mfa_setup_required and not yet enrolled)
 */
export default function GrcLogin({ onLogin }) {
  const [screen, setScreen] = useState('email-entry');
  const [email, setEmail] = useState('');
  const [firms, setFirms] = useState([]);
  const [activeFirm, setActiveFirm] = useState(null);
  const [password, setPassword] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const [mfaMethod, setMfaMethod] = useState('totp');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const emailRef = useRef(null);

  // ── email-entry ────────────────────────────────────────────────────────────
  const handleEmailLookup = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      const data = await lookupFirms(trimmed);
      if (data.error) {
        setError(data.error);
        return;
      }
      if (!data.firms || data.firms.length === 0) {
        setError('No auditor account found for this email address.');
        return;
      }
      if (data.firms.length === 1) {
        setActiveFirm(data.firms[0]);
        setScreen('password');
      } else {
        setFirms(data.firms);
        setScreen('firm-pick');
      }
    } catch (err) {
      setError(err.message || 'Lookup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── password ───────────────────────────────────────────────────────────────
  const handlePassword = async (e) => {
    e.preventDefault();
    if (!password || !activeFirm) return;
    setLoading(true);
    setError('');
    try {
      const data = await onLogin({ email: email.trim(), firmSlug: activeFirm.slug, password });
      // onLogin (useGrcAuth.login) returns the raw API response when status !== 'ok'
      if (!data) return; // session was set directly (status 'ok' path)
      if (data.status === 'ok') return; // already stored in useGrcAuth
      if (data.status === 'mfa-challenge') {
        setChallengeToken(data.challengeToken);
        setMfaMethod(data.method || 'totp');
        setScreen('mfa-challenge');
        return;
      }
      if (data.status === 'mfa-setup-required') {
        setChallengeToken(data.challengeToken);
        setScreen('mfa-setup');
        return;
      }
      if (data.error) setError(data.error);
    } catch (err) {
      setError(err.body?.error || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSuccess = (token, user) => {
    // Called by the MFA verify/setup screens — establish the session directly
    // by calling through to the parent's completeLogin (injected by GrcAuditorApp)
    onLogin.__complete?.(token, user);
  };

  const resetToEmail = () => {
    setScreen('email-entry');
    setPassword('');
    setActiveFirm(null);
    setFirms([]);
    setChallengeToken('');
    setError('');
    setTimeout(() => emailRef.current?.focus(), 0);
  };

  const outer = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg)',
    padding: '0 24px',
  };

  // ── MFA challenge ──────────────────────────────────────────────────────────
  if (screen === 'mfa-challenge') {
    const back = () => setScreen('password');
    return (
      <div style={outer}>
        <Header />
        {mfaMethod === 'webauthn' ? (
          <WebAuthnVerifyScreen
            challengeToken={challengeToken}
            onSuccess={handleMfaSuccess}
            onBack={back}
          />
        ) : (
          <TotpVerifyScreen
            challengeToken={challengeToken}
            onSuccess={handleMfaSuccess}
            onBack={back}
          />
        )}
      </div>
    );
  }

  // ── MFA setup ──────────────────────────────────────────────────────────────
  if (screen === 'mfa-setup') {
    return (
      <div style={outer}>
        <Header />
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
            Secure your account
          </div>
          <div className="section-label" style={{ marginTop: 4 }}>
            Set up two-factor authentication to continue
          </div>
        </div>
        <MfaSetupScreen
          challengeToken={challengeToken}
          onSuccess={handleMfaSuccess}
          onBack={() => setScreen('password')}
        />
      </div>
    );
  }

  // ── Firm picker ────────────────────────────────────────────────────────────
  if (screen === 'firm-pick') {
    return (
      <div style={outer}>
        <Header />
        <div className="card" style={{ padding: 32, width: '100%', maxWidth: 400 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)', marginBottom: 4 }}>
            Choose your firm
          </div>
          <div className="section-label" style={{ marginBottom: 20 }}>
            {email.trim()}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {firms.map((f) => (
              <button
                key={f.id}
                onClick={() => {
                  setActiveFirm(f);
                  setScreen('password');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderRadius: 8,
                  background: 'var(--bg2)',
                  border: '1px solid var(--border)',
                  cursor: 'pointer',
                  color: 'var(--fg)',
                  fontSize: 14,
                  fontFamily: 'var(--font-body)',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              >
                <span style={{ fontWeight: 500 }}>{f.name}</span>
                <span style={{ fontSize: 11, color: 'var(--fg3)', fontFamily: 'var(--font-mono)' }}>
                  {f.slug}
                </span>
              </button>
            ))}
          </div>
          <button
            onClick={resetToEmail}
            style={{
              marginTop: 20,
              background: 'none',
              border: 'none',
              color: 'var(--fg3)',
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
              padding: 0,
            }}
          >
            ← Use a different email
          </button>
        </div>
      </div>
    );
  }

  // ── Password entry ─────────────────────────────────────────────────────────
  if (screen === 'password') {
    return (
      <div style={outer}>
        <Header />
        <div className="card" style={{ padding: 32, width: '100%', maxWidth: 400 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)', marginBottom: 4 }}>
            Sign in to {activeFirm?.name}
          </div>
          <div className="section-label" style={{ marginBottom: 20 }}>
            {email.trim()}
          </div>
          <ErrorBanner message={error} />
          <form onSubmit={handlePassword}>
            <Field label="Password">
              <input
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
              />
            </Field>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !password}
              style={{ width: '100%', marginTop: 8, justifyContent: 'center' }}
            >
              {loading ? 'Signing in…' : 'Continue'}
            </button>
          </form>
          <button
            onClick={resetToEmail}
            style={{
              marginTop: 16,
              background: 'none',
              border: 'none',
              color: 'var(--fg3)',
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
              padding: 0,
            }}
          >
            ← Use a different email
          </button>
        </div>
      </div>
    );
  }

  // ── Email entry (default) ──────────────────────────────────────────────────
  return (
    <div style={outer}>
      <Header />
      <div className="card" style={{ padding: 32, width: '100%', maxWidth: 400 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)', marginBottom: 20 }}>
          Sign in to your firm
        </div>
        <ErrorBanner message={error} />
        <form onSubmit={handleEmailLookup}>
          <Field label="Email">
            <input
              ref={emailRef}
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              placeholder="you@firm.com"
            />
          </Field>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !email.trim()}
            style={{ width: '100%', marginTop: 8, justifyContent: 'center' }}
          >
            {loading ? 'Looking up…' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
